import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_CONFIG, BaseCrudService } from '@nebula-shared/data-access';
import {
  CreateEntidadBancariaPayload,
  CuentaBancariaListadoApi,
  EntidadBancariaListado,
  mapCuentaBancariaApiToUi,
  PageResponseEntidadBancaria,
  UpdateEntidadBancariaPayload,
} from '../../domain/bank-account.model';

@Injectable({ providedIn: 'root' })
export class BankAccountService extends BaseCrudService<
  EntidadBancariaListado,
  CreateEntidadBancariaPayload,
  UpdateEntidadBancariaPayload
> {
  constructor() {
    super();
    const config = API_CONFIG.treasury;
    this.setBaseUrl(`${config.baseUrl}${config.endpoints.cuentasBancarias}`);
  }

  private unwrap<T>(response: unknown): T {
    const root = response as Record<string, unknown> | null;
    if (!root || typeof root !== 'object') return response as T;
    const nested = root['response'];
    if (nested && typeof nested === 'object') {
      const content = (nested as Record<string, unknown>)['content'];
      if (content !== undefined) return content as T;
      return nested as T;
    }
    if (root['content'] !== undefined && !Array.isArray(root['content'])) {
      if (
        typeof root['totalElements'] === 'number' ||
        typeof root['page'] === 'number' ||
        typeof root['size'] === 'number'
      ) {
        return root as T;
      }
      return root['content'] as T;
    }
    return root as T;
  }

  getEntidadesBancariasPaginadas(
    page = 0,
    size = 10,
    searchTerm?: string,
    _sortColumn?: string,
    _sortDirection: 'ASC' | 'DESC' = 'ASC',
  ): Observable<PageResponseEntidadBancaria> {
    const term = searchTerm?.trim();
    const searchsBy = term
      ? ['codCuenta', 'numeroCuenta', 'nombreCuenta', 'noIdentificacion', 'banco'].map(
          (key, index) => ({
            key,
            operation: 'MATCH',
            value: term,
            linkOperator: index === 0 ? 'AND' : 'OR',
          }),
        )
      : [];
    const apiSortColumn: Record<string, string> = {
      codigo: 'codCuenta',
      nombre: 'nombreCuenta',
      terceroNombre: 'banco',
      terceroIdentificacion: 'noIdentificacion',
    };
    const ordersBy = _sortColumn
      ? [
          {
            column: apiSortColumn[_sortColumn] ?? _sortColumn,
            direction: _sortDirection,
          },
        ]
      : [];

    return this.http
      .post<unknown>(`${this.baseUrl}/page-response`, {
        page: String(page),
        size: String(size),
        searchsBy,
        ordersBy,
      })
      .pipe(
        map((response) => {
          const root = response as unknown as Record<string, unknown>;
          const responseData = (root['response'] as Record<string, unknown>) || root;
          const content = (responseData['content'] as Record<string, unknown>) || {};

          const rawList = Array.isArray(content['content'])
            ? (content['content'] as CuentaBancariaListadoApi[])
            : [];

          const items = rawList.map((row) => mapCuentaBancariaApiToUi(row));
          const totalElements = Number(content['totalElements'] ?? items.length) || 0;
          const pageSize = Number(content['size'] ?? size) || size;
          const pageNumber = Number(content['number'] ?? page) || 0;

          return {
            size: pageSize,
            number: pageNumber,
            numberOfElements: items.length,
            totalElements,
            totalPages: pageSize > 0 ? Math.max(1, Math.ceil(totalElements / pageSize)) : 1,
            content: items,
            empty: items.length === 0,
          };
        }),
      );
  }
}
